import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { HealthStatusBadge, MultiAssetHealthState } from '../../components/common/HealthStatusBadge';
import { Lifecycle5State } from '../../components/common/QuantitativeDataTable';

export interface MultiAssetHealthResponse {
  status: MultiAssetHealthState;
  timestamp_utc: string;
  uptime_seconds?: number;
  causal_lag_seconds?: number | null;
  database: {
    status: string;
    target_port?: number;
    port?: number;
    connected?: boolean;
    target?: string;
  };
  kill_switch: {
    active: boolean;
    env_var: string;
  };
  scheduler: {
    state: string;
    watchlist_cycles: number;
    macro_cycles: number;
    eod_cycles: number;
    last_eod_synced_date?: string | null;
  };
  providers: Record<string, {
    circuit?: string;
    circuit_state?: string;
    failures: number;
  }>;
}

export const MultiAssetHealthCenter: React.FC = () => {
  const [healthData, setHealthData] = useState<MultiAssetHealthResponse | null>(null);
  const [state, setState] = useState<Lifecycle5State>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const res = await apiClient.get<MultiAssetHealthResponse>('/api/v1/market/multiasset/health');
      if (!res || Object.keys(res).length === 0) {
        setHealthData(null);
        setState('empty');
      } else {
        setHealthData(res);
        if (res.status === 'DISCONNECTED') {
          setState('disconnected');
        } else if (res.status === 'STOPPED' || res.status === 'DEGRADED') {
          setState('stale');
        } else if (res.status === 'UNCONFIGURED') {
          setState('empty');
        } else {
          setState('fresh');
        }
      }
    } catch (err: any) {
      setHealthData(null);
      setState('disconnected');
      setErrorMessage(err?.message || '无法获取多资产健康遥测指标 (HTTP 503 / 离线)');
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const timer = setInterval(fetchHealth, 10000);
    return () => clearInterval(timer);
  }, [fetchHealth]);

  if (state === 'loading' && !healthData) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
        ⚡ 正在探测 MultiAsset 守护进程与存储状态...
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
        📭 暂无多资产健康遥测数据 (EMPTY)
      </div>
    );
  }

  if (state === 'disconnected') {
    return (
      <div
        style={{
          margin: '20px',
          padding: '24px',
          background: 'var(--alert-bearish-bg)',
          border: '1px solid var(--alert-bearish-border)',
          borderRadius: '8px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>🚨</div>
        <div style={{ fontWeight: 600, color: 'var(--color-bearish)', fontSize: '15px' }}>
          多资产调度与持久化中枢断开 (DISCONNECTED)
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '8px 0 16px 0' }}>
          {errorMessage || '后端 MultiAsset 无法连接或健康探针返回 503 Fail-Closed'}
        </div>
        <button
          onClick={fetchHealth}
          style={{
            padding: '6px 16px',
            background: 'var(--btn-primary-bg)',
            color: 'var(--btn-primary-text)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          立即重试
        </button>
      </div>
    );
  }

  const d = healthData!;

  return (
    <div className="multiasset-health-center" style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {/* Top Banner */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          padding: '16px',
          background: 'var(--bg-card)',
          borderRadius: '8px',
          border: '1px solid var(--border-card)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)' }}>
              🛡️ 多资产数据调度与存储健康中枢
            </h2>
            <HealthStatusBadge status={d.status} showDetails={true} lagSeconds={d.causal_lag_seconds} />
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            MultiAsset 物理隔离边界 · 真实 6 态流转 · 零凭证暴露
          </div>
        </div>

        <button
          onClick={fetchHealth}
          style={{
            padding: '4px 12px',
            borderRadius: '6px',
            background: 'var(--btn-primary-bg)',
            color: 'var(--btn-primary-text)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          刷新健康状态
        </button>
      </div>

      {/* Grid of Telemetry Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        {/* Card 1: Database Status */}
        <div
          style={{
            padding: '16px',
            background: 'var(--bg-card)',
            borderRadius: '8px',
            border: '1px solid var(--border-card)',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
            💾 物理存储边界 (Database)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>连接状态:</span>
              <strong style={{ color: (d.database?.connected ?? (d.database?.status === 'CONNECTED')) ? 'var(--color-emerald)' : 'var(--color-bearish)' }}>
                {(d.database?.connected ?? (d.database?.status === 'CONNECTED')) ? '● 已连接 (CONNECTED)' : '○ 离线 (DISCONNECTED)'}
              </strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>隔离目标实例:</span>
              <span>MULTIASSET_ACTIVE (MultiAsset exclusive)</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>底层引擎:</span>
              <span>TimescaleDB (Hypertable)</span>
            </div>
          </div>
        </div>

        {/* Card 2: Scheduler Telemetry */}
        <div
          style={{
            padding: '16px',
            background: 'var(--bg-card)',
            borderRadius: '8px',
            border: '1px solid var(--border-card)',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
            ⏱️ 调度守护进程 (Ingress Daemon)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>生命周期状态:</span>
              <strong style={{ color: 'var(--color-cyan)' }}>{d.scheduler?.state || 'UNCONFIGURED'}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>自选股 1m 轮询循环:</span>
              <span>{d.scheduler?.watchlist_cycles ?? 0} 次</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>宏观走廊轮询循环:</span>
              <span>{d.scheduler?.macro_cycles ?? 0} 次</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>EOD 日频选股同步:</span>
              <span>{d.scheduler?.eod_cycles ?? 0} 次 ({d.scheduler?.last_eod_synced_date || '未触发'})</span>
            </div>
          </div>
        </div>

        {/* Card 3: Providers Circuit Breakers */}
        <div
          style={{
            padding: '16px',
            background: 'var(--bg-card)',
            borderRadius: '8px',
            border: '1px solid var(--border-card)',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
            ⚡ 弹性数据源熔断器 (Provider Pool)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
            {Object.entries(d.providers || {}).map(([pName, pInfo]) => {
              const circuitVal = (pInfo.circuit_state || pInfo.circuit || 'CLOSED').toUpperCase();
              const isClosed = circuitVal === 'CLOSED';
              return (
                <div key={pName} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ textTransform: 'capitalize' }}>{pName}:</span>
                  <span
                    style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: isClosed ? 'var(--badge-bullish-bg)' : 'var(--badge-warning-bg)',
                      color: isClosed ? 'var(--color-emerald)' : 'var(--color-warning)',
                    }}
                  >
                    {circuitVal} (异常: {pInfo.failures ?? 0})
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Card 4: Kill Switch Guard */}
        <div
          style={{
            padding: '16px',
            background: 'var(--bg-card)',
            borderRadius: '8px',
            border: '1px solid var(--border-card)',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
            🛑 紧急熔断安全锁 (Kill Switch)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>熔断器状态:</span>
              <strong style={{ color: d.kill_switch?.active ? 'var(--color-bearish)' : 'var(--color-emerald)' }}>
                {d.kill_switch?.active ? '🔴 紧急熔断已触发 (ACTIVE)' : '🟢 就绪静默 (INACTIVE)'}
              </strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>配置环境变量:</span>
              <code style={{ fontSize: '11px' }}>{d.kill_switch?.env_var || 'MARKET_HUB_INGRESS_KILL_SWITCH'}</code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>上报时间戳:</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{d.timestamp_utc}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
